import { initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { 
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  enableNetwork,
  disableNetwork
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage } from "firebase/storage";
import { validateEnvironment } from './validateEnvironment';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
};


validateEnvironment();

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;
const appCheckProvider = process.env.REACT_APP_FIREBASE_APPCHECK_PROVIDER;
if (
  appCheckSiteKey &&
  process.env.NODE_ENV === 'development' &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)
) {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}
export const appCheck = appCheckSiteKey
  ? initializeAppCheck(app, {
      provider: appCheckProvider === 'enterprise'
        ? new ReCaptchaEnterpriseProvider(appCheckSiteKey)
        : new ReCaptchaV3Provider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    })
  : null;

// PHASE 3: Configure Firestore with modern persistent cache (multi-tab support)
// Use initializeFirestore instead of getFirestore when configuring cache
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});

// Initialize Functions with explicit region
export const functions = getFunctions(app, 'us-central1');
export const storage = getStorage(app);

// Optional: Connect to emulators in development; requires the .env variable REACT_APP_USE_EMULATORS to be set to true
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
    try {
        connectFirestoreEmulator(db, 'localhost', 8080);
        connectAuthEmulator(auth, 'http://localhost:9099');
        connectFunctionsEmulator(functions, 'localhost', 5001);
    } catch (e) {
        // Ignore if already connected
    }
}

// PHASE 3: Handle online/offline network states
// Automatically enable/disable Firestore network when connectivity changes
let isOnline = navigator.onLine;

window.addEventListener('online', () => {
  if (!isOnline) {
    console.log('[Firestore] Network connection restored, enabling Firestore network');
    enableNetwork(db).catch(err => {
      console.error('[Firestore] Failed to enable network:', err);
    });
    isOnline = true;
  }
});

window.addEventListener('offline', () => {
  if (isOnline) {
    console.log('[Firestore] Network connection lost, Firestore will use cached data');
    // Note: We don't call disableNetwork() here because Firestore handles it automatically
    // and we want to allow reconnection when network comes back
    isOnline = false;
  }
});

// Export network status helper
export function isFirestoreOnline() {
  return isOnline;
}

// Export manual network control (for testing or special cases)
export async function enableFirestoreNetwork() {
  try {
    await enableNetwork(db);
    console.log('[Firestore] Network manually enabled');
    return true;
  } catch (err) {
    console.error('[Firestore] Failed to enable network:', err);
    return false;
  }
}

export async function disableFirestoreNetwork() {
  try {
    await disableNetwork(db);
    console.log('[Firestore] Network manually disabled');
    return true;
  } catch (err) {
    console.error('[Firestore] Failed to disable network:', err);
    return false;
  }
}
